// ShipBuilder - Central logic for creating ships with equipment
// Post-overhaul: no ENGINE / SHIELD equipment. Movement and shield are native.
// Defaults give every ship a starter weapon and a couple of enhancers.

import {
  ShipClass,
  SlotDefinition,
  SHIPS,
  resolveShipId,
} from './ships.js';

import {
  Equipment,
  Weapon,
  Engine,
  Shield,
  getEquipmentById,
} from './equipment.js';

export interface BuiltShip {
  shipClassId: string;
  shipClass: ShipClass;
  slots: SlotDefinition[];
  stats: {
    hull: number;
    maxHull: number;
    shield: number;         // legacy: 0 in new model (no HP shield)
    maxShield: number;      // legacy: 0
    acceleration: number;
    maxSpeed: number;
    strafeAcceleration: number;
    rotationSpeed: number;
    brakingPower: number;
    totalPower: number;        // sum of equipment.power (no longer includes basePower)
    powerCapacity: number;     // ship's cap
    heatCapacity: number;      // base + heat dissipator bonuses
    heatDissipation: number;   // base + heat dissipator bonuses
    cargoCapacity: number;     // base + cargo expander bonuses
    sensorRange: number;       // base + sensor array bonuses
    shieldRadius: number;      // base + shield amplifier bonuses
  };
  equippedWeapon: Weapon | null;
  equippedEngine: Engine | null;   // always null in new model
  equippedShield: Shield | null;   // always null in new model
}

// Default equipment loadouts for ships
// Keys are canonical ship IDs (corvette_t1, ...) — applyLoadout supports arrays per slot type.
export const DEFAULT_LOADOUTS: Record<string, Partial<Record<string, string | string[]>>> = {
  // Corvette
  corvette_t1: {
    WEAPON: 'weapon_kinetic_t1',
    TOOL: 'tool_mining_t1',
    ENHANCER: ['enhancer_sensor_t1', 'enhancer_cargo_t1'],
  },
  corvette_t2: {
    WEAPON: ['weapon_kinetic_t2'],
    TOOL: 'tool_mining_t1',
    ENHANCER: ['enhancer_sensor_t1', 'enhancer_booster_t2'],
  },
  corvette_t3: {
    WEAPON: ['weapon_kinetic_t2'],
    TOOL: 'tool_mining_t2',
    ENHANCER: ['enhancer_sensor_t2', 'enhancer_booster_t2'],
  },
  corvette_t4: {
    WEAPON: ['weapon_energy_t3'],
    TOOL: 'tool_mining_t2',
    ENHANCER: ['enhancer_sensor_t3', 'enhancer_booster_t3', 'enhancer_heat_t2'],
  },

  // Frigate
  frigate_t1: {
    WEAPON: ['weapon_kinetic_t1', 'weapon_kinetic_t1'],
    TOOL: 'tool_mining_t1',
    ENHANCER: ['enhancer_sensor_t1', 'enhancer_booster_t1'],
  },
  frigate_t2: {
    WEAPON: ['weapon_energy_t2'],
    TOOL: 'tool_mining_t1',
    ENHANCER: ['enhancer_sensor_t1', 'enhancer_booster_t2'],
  },
  frigate_t3: {
    WEAPON: ['weapon_energy_t2', 'weapon_kinetic_t2'],
    TOOL: 'tool_mining_t2',
    ENHANCER: ['enhancer_sensor_t2', 'enhancer_booster_t2'],
  },
  frigate_t4: {
    WEAPON: ['weapon_energy_t3'],
    TOOL: 'tool_mining_t2',
    ENHANCER: ['enhancer_sensor_t3', 'enhancer_booster_t3', 'enhancer_heat_t2'],
  },

  // Destroyer
  destroyer_t1: {
    WEAPON: ['weapon_kinetic_t1'],
    TOOL: 'tool_mining_t1',
    ENHANCER: ['enhancer_hull_t1', 'enhancer_sensor_t1'],
  },
  destroyer_t2: {
    WEAPON: ['weapon_energy_t2'],
    TOOL: 'tool_mining_t1',
    ENHANCER: ['enhancer_hull_t1', 'enhancer_sensor_t1'],
  },
  destroyer_t3: {
    WEAPON: ['weapon_energy_t2', 'weapon_kinetic_t2'],
    TOOL: 'tool_mining_t2',
    ENHANCER: ['enhancer_hull_t2', 'enhancer_sensor_t2', 'enhancer_heat_t1'],
  },
  destroyer_t4: {
    WEAPON: ['weapon_energy_t3', 'weapon_kinetic_t3'],
    TOOL: 'tool_mining_t2',
    ENHANCER: ['enhancer_hull_t3', 'enhancer_sensor_t3', 'enhancer_heat_t2'],
  },

  // Cruiser
  cruiser_t1: {
    WEAPON: ['weapon_energy_t1'],
    TOOL: 'tool_mining_t1',
    ENHANCER: ['enhancer_hull_t1', 'enhancer_sensor_t1'],
  },
  cruiser_t2: {
    WEAPON: ['weapon_energy_t2'],
    TOOL: 'tool_mining_t2',
    ENHANCER: ['enhancer_hull_t2', 'enhancer_sensor_t2'],
  },
  cruiser_t3: {
    WEAPON: ['weapon_energy_t2', 'weapon_kinetic_t2'],
    TOOL: 'tool_mining_t3',
    ENHANCER: ['enhancer_hull_t2', 'enhancer_sensor_t2', 'enhancer_heat_t1'],
  },
  cruiser_t4: {
    WEAPON: ['weapon_energy_t3', 'weapon_kinetic_t3'],
    TOOL: 'tool_mining_t3',
    ENHANCER: ['enhancer_shield_amp_t1', 'enhancer_hull_t3', 'enhancer_sensor_t3'],
  },

  // Battleship
  battleship_t1: {
    WEAPON: ['weapon_kinetic_t1'],
    TOOL: 'tool_mining_t1',
    ENHANCER: ['enhancer_hull_t1', 'enhancer_heat_t1'],
  },
  battleship_t2: {
    WEAPON: ['weapon_energy_t2'],
    TOOL: 'tool_mining_t1',
    ENHANCER: ['enhancer_hull_t2', 'enhancer_heat_t1'],
  },
  battleship_t3: {
    WEAPON: ['weapon_energy_t3', 'weapon_kinetic_t2'],
    TOOL: 'tool_mining_t2',
    ENHANCER: ['enhancer_shield_amp_t1', 'enhancer_hull_t3', 'enhancer_heat_t2'],
  },
  battleship_t4: {
    WEAPON: ['weapon_energy_t3', 'weapon_kinetic_t3'],
    TOOL: 'tool_mining_t3',
    ENHANCER: ['enhancer_shield_amp_t2', 'enhancer_hull_t3', 'enhancer_heat_t2'],
  },

  // Carrier
  carrier_t1: {
    WEAPON: ['weapon_energy_t1'],
    TOOL: 'tool_mining_t1',
    ENHANCER: ['enhancer_hull_t1', 'enhancer_sensor_t1'],
    UTILITY: ['utility_miner_bay_t1'],
  },
  carrier_t2: {
    WEAPON: ['weapon_energy_t2'],
    TOOL: 'tool_mining_t2',
    ENHANCER: ['enhancer_hull_t2', 'enhancer_sensor_t2'],
    UTILITY: ['utility_miner_bay_t2'],
  },
  carrier_t3: {
    WEAPON: ['weapon_energy_t2'],
    TOOL: 'tool_mining_t2',
    ENHANCER: ['enhancer_shield_amp_t1', 'enhancer_hull_t2', 'enhancer_sensor_t2'],
    UTILITY: ['utility_fighter_bay_t2', 'utility_miner_bay_t2'],
  },
  carrier_t4: {
    WEAPON: ['weapon_energy_t3'],
    TOOL: 'tool_mining_t3',
    ENHANCER: ['enhancer_shield_amp_t2', 'enhancer_hull_t3', 'enhancer_sensor_t3'],
    UTILITY: ['utility_fighter_bay_t3', 'utility_miner_bay_t3'],
  },

  // Dreadnought
  dreadnought_t1: {
    WEAPON: ['weapon_energy_t2', 'weapon_kinetic_t2'],
    TOOL: 'tool_mining_t1',
    ENHANCER: ['enhancer_hull_t1', 'enhancer_heat_t1'],
  },
  dreadnought_t2: {
    WEAPON: ['weapon_energy_t2', 'weapon_kinetic_t2'],
    TOOL: 'tool_mining_t2',
    ENHANCER: ['enhancer_shield_amp_t1', 'enhancer_hull_t2', 'enhancer_heat_t2'],
  },
  dreadnought_t3: {
    WEAPON: ['weapon_energy_t3', 'weapon_kinetic_t3'],
    TOOL: 'tool_mining_t2',
    ENHANCER: ['enhancer_shield_amp_t2', 'enhancer_hull_t3', 'enhancer_heat_t2'],
  },
  dreadnought_t4: {
    WEAPON: ['weapon_energy_t4', 'weapon_kinetic_t3'],
    TOOL: 'tool_mining_t3',
    ENHANCER: ['enhancer_shield_amp_t3', 'enhancer_hull_t4', 'enhancer_heat_t3'],
  },

  // Hauler
  hauler_t1: {
    WEAPON: ['weapon_kinetic_t1'],
    ENHANCER: ['enhancer_cargo_t1', 'enhancer_hull_t1'],
  },
  hauler_t2: {
    WEAPON: ['weapon_kinetic_t1'],
    ENHANCER: ['enhancer_cargo_t2', 'enhancer_hull_t1'],
  },
  hauler_t3: {
    WEAPON: ['weapon_kinetic_t2'],
    ENHANCER: ['enhancer_cargo_t3', 'enhancer_hull_t2'],
  },
  hauler_t4: {
    WEAPON: ['weapon_kinetic_t2'],
    ENHANCER: ['enhancer_cargo_t4', 'enhancer_hull_t3'],
  },
};

// Legacy ID aliases — let existing seed data / DB rows continue to work
const LEGACY_SHIP_IDS_FOR_LOADOUT: Record<string, string> = {
  sparrow: 'corvette_t1', swift: 'corvette_t2', falcon: 'corvette_t3', peregrine: 'corvette_t4',
  corsair: 'frigate_t1', raider: 'frigate_t2', stalker: 'frigate_t3', phantom: 'frigate_t4',
  sentinel: 'destroyer_t1', guardian: 'destroyer_t2', warden: 'destroyer_t3', vindicator: 'destroyer_t4',
  pioneer: 'cruiser_t1', voyager: 'cruiser_t2', pathfinder: 'cruiser_t3', odyssey: 'cruiser_t4',
  bulwark: 'battleship_t1', fortress: 'battleship_t2', juggernaut: 'battleship_t3', leviathan: 'battleship_t4',
  nest: 'carrier_t1', hive: 'carrier_t2', swarm: 'carrier_t3', armada: 'carrier_t4',
  titan: 'dreadnought_t1', colossus: 'dreadnought_t2', behemoth: 'dreadnought_t3', sovereign: 'dreadnought_t4',
  mule: 'hauler_t1', nomad: 'hauler_t2', caravan: 'hauler_t3', mammoth: 'hauler_t4',
};
for (const [legacy, canonical] of Object.entries(LEGACY_SHIP_IDS_FOR_LOADOUT)) {
  if (DEFAULT_LOADOUTS[canonical] && !DEFAULT_LOADOUTS[legacy]) {
    DEFAULT_LOADOUTS[legacy] = DEFAULT_LOADOUTS[canonical];
  }
}

export class ShipBuilder {
  private shipClassId: string;
  private shipClass: ShipClass;
  private slots: SlotDefinition[];

  private constructor(shipClassId: string) {
    const canonical = resolveShipId(shipClassId);
    const shipClass = SHIPS[canonical] ?? SHIPS[shipClassId];
    if (!shipClass) {
      throw new Error(`Unknown ship class: ${shipClassId}`);
    }

    this.shipClassId = canonical;
    this.shipClass = shipClass;
    this.slots = shipClass.slots.map(slot => ({ ...slot }));
  }

  static create(shipClassId: string): ShipBuilder {
    return new ShipBuilder(shipClassId);
  }

  withDefaultLoadout(): ShipBuilder {
    const loadout = DEFAULT_LOADOUTS[this.shipClassId];
    if (loadout) {
      this.applyLoadout(loadout);
    }
    return this;
  }

  withLoadout(loadout: Partial<Record<string, string | string[]>>): ShipBuilder {
    this.applyLoadout(loadout);
    return this;
  }

  equip(slotType: string, equipmentId: string): ShipBuilder {
    const slot = this.slots.find(s => s.type === slotType && !s.equipped);
    if (slot) {
      slot.equipped = equipmentId;
    }
    return this;
  }

  equipSlot(slotIndex: number, equipmentId: string): ShipBuilder {
    if (slotIndex >= 0 && slotIndex < this.slots.length) {
      this.slots[slotIndex].equipped = equipmentId;
    }
    return this;
  }

  clearAllEquipment(): ShipBuilder {
    this.slots.forEach(slot => {
      slot.equipped = undefined;
    });
    return this;
  }

  build(): BuiltShip {
    const stats = this.calculateStats();
    return {
      shipClassId: this.shipClassId,
      shipClass: this.shipClass,
      slots: this.slots,
      stats,
      equippedWeapon: this.getEquippedByCategory('WEAPON') as Weapon | null,
      equippedEngine: null,
      equippedShield: null,
    };
  }

  buildSlots(): SlotDefinition[] {
    return this.slots;
  }

  private applyLoadout(loadout: Partial<Record<string, string | string[]>>): void {
    for (const [slotType, equipmentIds] of Object.entries(loadout)) {
      if (!equipmentIds) continue;
      const items = Array.isArray(equipmentIds) ? equipmentIds : [equipmentIds];
      for (const equipmentId of items) {
        const slot = this.slots.find(s => s.type === slotType && !s.equipped);
        if (slot) {
          slot.equipped = equipmentId;
        }
      }
    }
  }

  private getEquippedByCategory(category: string): Equipment | null {
    for (const slot of this.slots) {
      if (!slot.equipped) continue;
      const equipment = getEquipmentById(slot.equipped);
      if (equipment && equipment.category === category) {
        return equipment;
      }
    }
    return null;
  }

  private calculateStats(): BuiltShip['stats'] {
    const baseStats = this.shipClass.stats;

    let hull = baseStats.hull;
    let totalPower = 0;
    let heatCapacity = baseStats.heatCapacity;
    let heatDissipation = baseStats.heatDissipation;
    let cargoCapacity = baseStats.cargoCapacity;
    let sensorRange = baseStats.sensorRange;
    let shieldRadius = baseStats.shieldRadius;

    for (const slot of this.slots) {
      if (!slot.equipped) continue;
      const equipment = getEquipmentById(slot.equipped);
      if (!equipment) continue;

      totalPower += equipment.power;

      if (equipment.category === 'ENHANCER') {
        const en = equipment as any;
        if (en.stats.hullBonus) hull += en.stats.hullBonus;
        if (en.stats.cargoBonus) cargoCapacity += en.stats.cargoBonus;
        if (en.stats.sensorBonus) sensorRange += en.stats.sensorBonus;
        if (en.stats.heatCapacityBonus) heatCapacity += en.stats.heatCapacityBonus;
        if (en.stats.heatDissipationBonus) heatDissipation += en.stats.heatDissipationBonus;
        if (en.stats.shieldRadiusBonus) shieldRadius += en.stats.shieldRadiusBonus;
      }
    }

    return {
      hull,
      maxHull: hull,
      shield: 0,
      maxShield: 0,
      acceleration: baseStats.baseAcceleration,
      maxSpeed: baseStats.baseMaxSpeed,
      strafeAcceleration: baseStats.baseStrafeAcceleration,
      rotationSpeed: baseStats.rotationSpeed,
      brakingPower: baseStats.brakingPower,
      totalPower,
      powerCapacity: baseStats.powerCapacity,
      heatCapacity,
      heatDissipation,
      cargoCapacity,
      sensorRange,
      shieldRadius,
    };
  }
}

// ============================================
// Helpers
// ============================================

// Total power consumed by equipped items (sum of equipment.power).
// NOTE: post-overhaul this does NOT include basePower. Compare against
// SHIPS[id].stats.powerCapacity for the cap check.
export function calculateTotalPower(shipClassId: string, slots: SlotDefinition[]): number {
  let totalPower = 0;
  for (const slot of slots) {
    if (!slot.equipped) continue;
    const equipment = getEquipmentById(slot.equipped);
    if (equipment) {
      totalPower += equipment.power;
    }
  }
  return totalPower;
}

export function buildPlayerShip(shipClassId: string): BuiltShip {
  return ShipBuilder.create(shipClassId)
    .withDefaultLoadout()
    .build();
}

export function getEquippedWeaponId(slots: SlotDefinition[]): string | null {
  for (const slot of slots) {
    if (slot.type === 'WEAPON' && slot.equipped) {
      return slot.equipped;
    }
  }
  return null;
}

// Engine no longer exists; kept as no-op for callers that haven't migrated yet.
export function getEquippedEngineId(_slots: SlotDefinition[]): string | null {
  return null;
}

export function getEquippedBoosterId(slots: SlotDefinition[]): string | null {
  for (const slot of slots) {
    if (slot.type === 'ENHANCER' && slot.equipped) {
      const equipment = getEquipmentById(slot.equipped);
      if (equipment && equipment.category === 'ENHANCER') {
        const enhancer = equipment as any;
        if (enhancer.enhancerType === 'booster') {
          return slot.equipped;
        }
      }
    }
  }
  return null;
}

export function getEquippedSensorId(slots: SlotDefinition[]): string | null {
  for (const slot of slots) {
    if (slot.type === 'ENHANCER' && slot.equipped) {
      const equipment = getEquipmentById(slot.equipped);
      if (equipment && equipment.category === 'ENHANCER') {
        const enhancer = equipment as any;
        if (enhancer.enhancerType === 'sensor_array') {
          return slot.equipped;
        }
      }
    }
  }
  return null;
}
